import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, inject, Injector, Input, Output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { UiIconsList, UiIconsModule } from 'ui-icons';

import { ANGULAR_LOCALE_SERVICE_TOKEN } from '@ui-platform/lib/constants/tokens';

type Value = number | string | null;

@Component({
  selector: 'ui-platform-input',
  templateUrl: './input.component.html',
  styleUrl: './input.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    FormsModule,
    NzInputModule,
    NzSpinModule,
    NgxMaskDirective,
    UiIconsModule
  ],
  providers: [
    provideNgxMask(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiPlatformInputComponent),
      multi: true
    },
    {
      provide: ANGULAR_LOCALE_SERVICE_TOKEN,
      useValue: {
        decimalMarker: ',',
        thousandSeparator: '===',
        defaultLang: 'en',
        locale: 'en'
      }
    }
  ]
})
export class UiPlatformInputComponent implements ControlValueAccessor {
  languageProps = inject(ANGULAR_LOCALE_SERVICE_TOKEN, { skipSelf: true, optional: true }) ?? {
    decimalMarker: ',',
    thousandSeparator: '◦', // keep here something visible in case the token is not provided from app
    defaultLang: 'en',
    locale: 'en'
  };
  injector = inject(Injector);

  @Input() alignTo: 'left' | 'right' | 'center' = 'left';
  @Input() borderless = false;
  @Input() disabled = false;
  @Input() inProgress = false;
  @Input() label = '';
  @Input() labelSuffixText!: string;
  @Input() maxLength: number | null = null;
  @Input() placeholder = '';
  // TODO type number should return number / null, not an empty string
  @Input() type = 'text';
  @Input({ required: false }) actionLink: string | undefined;
  @Input() allowNegativeNumbers = false;

  @Input() mask = 'separator.2';

  @Output() actionLinkClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() blured: EventEmitter<void> = new EventEmitter<void>();
  @Output() focused: EventEmitter<void> = new EventEmitter<void>();

  internalValue: Value = null;
  passwordVisible = false;

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  onFocus(): void {
    this.focused.emit();
  }

  onBlur(): void {
    this.syncExternalTouched();
    this.blured.emit();
  }

  syncExternalValue: (value: Value) => void = () => {};
  syncExternalTouched: () => void = () => {};

  onModelChange(value: Value) {
    const convertedValue = this.type === 'percentage' && value ? parseFloat(value as string) / 100 : value;
    this.syncExternalValue(convertedValue);
    this.syncExternalTouched();
  }

  writeValue(value: Value) {
    this.internalValue = this.type === 'percentage' && value ? parseFloat(value as string) * 100 : value;
    this.changeDetectorRef.detectChanges();
  }

  registerOnChange(fn: (value: Value) => void) {
    this.syncExternalValue = (value: Value) => {
      fn(value);
    };
  }

  registerOnTouched(fn: () => void) {
    this.syncExternalTouched = () => {
      fn();
    };
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.changeDetectorRef.detectChanges();
  }

  onActionLinkClicked(): void {
    this.actionLinkClicked.emit();
  }

  get nzType() {
    if (this.type === 'password') {
      return this.passwordVisible ? 'text' : 'password';
    }

    return this.type;
  }

  get suffixIcon(): UiIconsList {
    return (this.passwordVisible ? 'eye-hide' : 'eye-show') as UiIconsList;
  }
}
